/**
 * Request Dashboard.
 *
 * Surfaces the per-project KV counters populated by the request-metrics
 * middleware in `aapastech-backend/src/index.ts`. Shows total/2xx/4xx/5xx
 * by day, average latency, and summary cards for the selected window.
 *
 * Only API requests whose URL carried the project ID (plugin, webhook,
 * analytics, audit, chatbot, blog, contact, consent routes) are counted.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Activity, AlertTriangle, ServerCrash, Gauge, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface DailyBucket {
  date: string;
  total: number;
  s2xx: number;
  s3xx: number;
  s4xx: number;
  s5xx: number;
  avgLatencyMs: number;
}

interface Summary {
  total: number;
  s2xx: number;
  s4xx: number;
  s5xx: number;
  errorRate: number;
  avgLatencyMs: number;
}

const WINDOWS: Array<{ label: string; days: number }> = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function formatPct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

function StatCard({
  icon,
  label,
  value,
  sublabel,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  tone?: 'default' | 'warn' | 'danger';
}) {
  const toneCls =
    tone === 'danger' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-foreground';
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`${toneCls}`}>{icon}</div>
        <div className="flex-1">
          <div className="text-muted-foreground text-xs uppercase tracking-wide">{label}</div>
          <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
          {sublabel && <div className="text-muted-foreground text-xs">{sublabel}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RequestDashboardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [days, setDays] = useState(30);

  const query = useQuery({
    queryKey: ['request-metrics', projectId, days],
    queryFn: async () => {
      const { data } = await api.get(`/analytics/${projectId}/requests?days=${days}`);
      return data.data as {
        days: number;
        series: DailyBucket[];
        summary: Summary;
      };
    },
    enabled: Boolean(projectId),
  });

  const series = query.data?.series ?? [];
  const summary = query.data?.summary;

  // Recharts wants short labels on X axis.
  const chartData = series.map((d) => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <Activity className="h-7 w-7" /> Request Dashboard
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Daily API request volume, errors, and latency for this project. Data is kept for 60
            days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <Button
              key={w.days}
              variant={days === w.days ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(w.days)}
            >
              {w.label}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <StatCard
              icon={<Activity className="h-8 w-8" />}
              label="Total requests"
              value={formatNum(summary?.total ?? 0)}
              sublabel={`Last ${days} days`}
            />
            <StatCard
              icon={<AlertTriangle className="h-8 w-8" />}
              label="Client errors (4xx)"
              value={formatNum(summary?.s4xx ?? 0)}
              tone={summary && summary.s4xx > 0 ? 'warn' : 'default'}
            />
            <StatCard
              icon={<ServerCrash className="h-8 w-8" />}
              label="Server errors (5xx)"
              value={formatNum(summary?.s5xx ?? 0)}
              tone={summary && summary.s5xx > 0 ? 'danger' : 'default'}
            />
            <StatCard
              icon={<Gauge className="h-8 w-8" />}
              label="Error rate"
              value={formatPct(summary?.errorRate ?? 0)}
              sublabel={`Avg latency: ${summary?.avgLatencyMs ?? 0} ms`}
              tone={
                summary && summary.errorRate > 0.05
                  ? 'danger'
                  : summary && summary.errorRate > 0.01
                    ? 'warn'
                    : 'default'
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requests by day</CardTitle>
              <CardDescription>
                Stacked by status class. Line shows average latency in milliseconds.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.total === 0 ? (
                <p className="text-muted-foreground py-12 text-center text-sm">
                  No requests recorded yet. The dashboard fills in once this project starts
                  receiving traffic.
                </p>
              ) : (
                <div className="h-80 w-full">
                  <ResponsiveContainer>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        label={{ value: 'ms', angle: -90, position: 'insideRight', fontSize: 11 }}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="s2xx"
                        stackId="status"
                        fill="#22c55e"
                        name="2xx"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="s3xx"
                        stackId="status"
                        fill="#94a3b8"
                        name="3xx"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="s4xx"
                        stackId="status"
                        fill="#f59e0b"
                        name="4xx"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="s5xx"
                        stackId="status"
                        fill="#ef4444"
                        name="5xx"
                      />
                      <Line
                        yAxisId="right"
                        dataKey="avgLatencyMs"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={false}
                        name="avg latency"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Daily breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left text-xs uppercase tracking-wide">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Total</th>
                      <th className="pb-2 pr-4 font-medium">2xx</th>
                      <th className="pb-2 pr-4 font-medium">3xx</th>
                      <th className="pb-2 pr-4 font-medium">4xx</th>
                      <th className="pb-2 pr-4 font-medium">5xx</th>
                      <th className="pb-2 font-medium">Avg latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...series].reverse().map((d) => (
                      <tr key={d.date} className="border-b last:border-b-0">
                        <td className="py-2 pr-4 font-mono text-xs">{d.date}</td>
                        <td className="py-2 pr-4">{d.total.toLocaleString()}</td>
                        <td className="py-2 pr-4 text-green-600">{d.s2xx.toLocaleString()}</td>
                        <td className="text-muted-foreground py-2 pr-4">
                          {d.s3xx.toLocaleString()}
                        </td>
                        <td className="py-2 pr-4 text-amber-600">{d.s4xx.toLocaleString()}</td>
                        <td className="py-2 pr-4 text-red-600">{d.s5xx.toLocaleString()}</td>
                        <td className="text-muted-foreground py-2">{d.avgLatencyMs} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
